import React from 'react';
import { motion } from 'framer-motion';
import { 
  ShoppingBag, 
  Shield, 
  CreditCard, 
  Users,
  Lock,
  Database,
  Network,
  Cloud,
  CheckCircle,
  ArrowRight,
  AlertTriangle,
  Truck
} from 'lucide-react';

interface RetailProps {
  setCurrentPage: (page: string) => void;
}

const Retail: React.FC<RetailProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "E-commerce Security",
      description: "Comprehensive security solutions for online retail operations.",
      items: [
        "Secure payment processing",
        "Fraud prevention",
        "DDoS protection",
        "SSL/TLS encryption"
      ]
    },
    {
      icon: CreditCard,
      title: "Payment Security",
      description: "Advanced protection for payment systems and transaction data.",
      items: [
        "PCI DSS compliance",
        "Point-of-Sale security",
        "Tokenization",
        "End-to-end encryption"
      ]
    },
    {
      icon: Users,
      title: "Customer Data Protection",
      description: "Robust security measures to protect sensitive customer information.",
      items: [
        "Data encryption",
        "Access controls",
        "Privacy compliance",
        "Data loss prevention"
      ]
    },
    {
      icon: Network,
      title: "Network Security",
      description: "Secure retail networks and infrastructure protection.",
      items: [
        "Network monitoring",
        "Intrusion detection",
        "Firewall management",
        "Endpoint security"
      ]
    }
  ];

  const solutions = [
    {
      icon: Lock,
      title: "Fraud Prevention",
      items: [
        "Real-time fraud detection",
        "Transaction monitoring",
        "Behavioral analysis",
        "Risk assessment"
      ]
    },
    {
      icon: Cloud,
      title: "Cloud Security",
      items: [
        "Cloud infrastructure",
        "Data protection",
        "Access management",
        "Compliance monitoring"
      ]
    },
    {
      icon: Truck,
      title: "Supply Chain Security",
      items: [
        "Vendor security",
        "Logistics protection",
        "Inventory security",
        "Third-party risk management"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Database,
      title: "Data Management",
      description: "Secure customer and transaction data with advanced protection measures.",
      features: [
        "Customer data security",
        "Transaction protection",
        "Data governance",
        "Compliance management"
      ]
    },
    {
      icon: Shield,
      title: "Threat Protection",
      description: "Comprehensive threat detection and prevention for retail environments.",
      features: [
        "Threat monitoring",
        "Attack prevention",
        "Security analytics",
        "Incident response"
      ]
    },
    {
      icon: AlertTriangle,
      title: "Risk Management",
      description: "Proactive risk assessment and management for retail operations.",
      features: [
        "Risk assessment",
        "Compliance monitoring",
        "Security audits",
        "Policy enforcement"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <ShoppingBag className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Retail Security Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Advanced cybersecurity solutions designed specifically for retail businesses, protecting your customers, transactions, and operations
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Solutions Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Security Solutions</h2>
            <p className="text-lg text-muted">
              Comprehensive security solutions for modern retail
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <solution.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{solution.title}</h3>
                </div>
                
                <ul className="space-y-3">
                  {solution.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Advanced Capabilities</h2>
            <p className="text-lg text-muted">
              State-of-the-art security capabilities for retail environments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{capability.description}</p>

                <ul className="space-y-3">
                  {capability.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Secure Your Retail Business</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our retail security experts to discuss your business's specific needs and discover how we can help protect your operations
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Retail;